import React, { useCallback, useEffect, useRef, useState } from "react";
import { Edit2, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import UserForm from "./UserForm";
import {
  activePremium,
  createUser,
  deleteUser,
  getUsers,
  importUsers as importUsersService,
  bulkUpdatePremium,
} from "../../services/UserService";
import { Tooltip } from "@mui/material";
import DeleteConfirmModal from "../common/DeleteConfirmModal";
import * as XLSX from "xlsx";

const configDate = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: "Asia/Ho_Chi_Minh", // hoặc remove nếu dùng UTC
};

export default function Users() {
  // ]);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "",
    fullName: "",
    phoneNumber: "",
    yearOfBirth: "",
    city: "",
    school: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 6;
  const [isEditing, setIsEditing] = useState(null);
  const [isSearch, setIsSearch] = useState(false);
  const [listUsers, setListUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
    userName: "",
  });
  const [isImporting, setIsImporting] = useState(false);
  const [isUpdatingPremium, setIsUpdatingPremium] = useState(false);
  const fileInputRef = useRef(null);
  const premiumFileInputRef = useRef(null);

  const handleFetch = useCallback(async () => {
    try {
      const response = await getUsers(currentPage, limit, searchQuery);
      setListUsers(response?.data);
      setTotalPages(response?.totalPages);
      setCurrentPage(response?.currentPage);
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  }, [currentPage, limit, searchQuery]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  const handleEditUser = (user) => {
    setIsEditing(true);
    setFormData({
      ...user,
      courses: user.courses || [], // Đảm bảo courses được set khi edit
    });
  };

  const handleDeleteUser = (id, userName) => {
    setDeleteModal({
      isOpen: true,
      userId: id,
      userName: userName || "người dùng này",
    });
  };

  const confirmDeleteUser = async () => {
    try {
      const res = await deleteUser(deleteModal.userId);
      toast.success(res?.message);
      setListUsers(
        listUsers.filter((user) => user?._id !== deleteModal.userId)
      );
      setDeleteModal({ isOpen: false, userId: null, userName: "" });
      handleFetch();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
      setDeleteModal({ isOpen: false, userId: null, userName: "" });
    }
  };

  const handleDownloadTemplate = () => {
    const sampleData = [
      {
        email: "example1@gmail.com",
        password: "123456",
        fullName: "Nguyễn Văn A",
        phoneNumber: "0912345678",
        yearOfBirth: "2005",
        city: "Hà Nội",
        school: "THPT PIMAX",
        role: 0,
        premium: false,
      },
      {
        email: "example2@gmail.com",
        password: "123456",
        fullName: "Nguyễn Văn A",
        phoneNumber: "0912345678",
        yearOfBirth: "2005",
        city: "Hà Nội",
        school: "THPT PIMAX",
        role: 0,
        premium: true,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, `pimax_users_template_${Date.now()}.xlsx`);
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleBulkPremiumClick = () => {
    if (premiumFileInputRef.current) {
      premiumFileInputRef.current.value = "";
      premiumFileInputRef.current.click();
    }
  };

  const normalizeFieldValue = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "number") {
      return value.toString();
    }
    return value.toString().trim();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (!rows || rows.length === 0) {
        toast.error("File Excel không có dữ liệu");
        return;
      }

      const fieldMapping = {
        email: ["email", "e-mail", "mail"],
        password: ["password", "matkhau", "pass"],
        fullName: ["fullname", "full name", "ten", "tennguoidung", "name"],
        phoneNumber: [
          "phonenumber",
          "phone",
          "sodienthoai",
          "so dien thoai",
          "phone number",
        ],
        yearOfBirth: [
          "yearofbirth",
          "namsinh",
          "nam sinh",
          "birthyear",
          "birth year",
        ],
        city: ["city", "thanhpho", "thanh pho", "tinh/thanh"],
        school: ["school", "truong", "truonghoc", "truong hoc"],
        role: ["role", "quyen"],
        premium: ["premium", "isPremium", "is premium", "vip"],
      };

      const getValueByKeys = (rowMap, keys) => {
        for (const key of keys) {
          if (rowMap.hasOwnProperty(key)) {
            return rowMap[key];
          }
        }
        return "";
      };

      const normalizedUsers = [];
      const seenEmails = new Set();
      const rowErrors = [];

      rows.forEach((row, index) => {
        const rowNumber = index + 2; // header is row 1
        const normalizedRow = {};

        const rowMap = {};
        Object.keys(row).forEach((key) => {
          const normalizedKey = normalizeFieldValue(key).toLowerCase();
          rowMap[normalizedKey] = row[key];
        });

        Object.entries(fieldMapping).forEach(([targetField, keys]) => {
          const value = getValueByKeys(rowMap, keys);
          normalizedRow[targetField] = normalizeFieldValue(value);
        });

        const requiredFields = [
          "email",
          "password",
          "fullName",
          "phoneNumber",
          "yearOfBirth",
          "city",
          "school",
          "role",
        ];

        requiredFields.forEach((field) => {
          if (!normalizedRow[field]) {
            rowErrors.push(`Dòng ${rowNumber}: Thiếu trường ${field}`);
          }
        });

        const isValidEmail = (email) =>
          /^[a-zA-Z](?!.*\.\.)[a-zA-Z0-9._-]*[a-zA-Z0-9]@gmail\.com$/.test(
            email
          );
        const email = normalizedRow.email?.toLowerCase();
        if (email) {
          if (!isValidEmail(email)) {
            rowErrors.push(`Dòng ${rowNumber}: Email không hợp lệ (${email})`);
          } else if (seenEmails.has(email)) {
            rowErrors.push(
              `Dòng ${rowNumber}: Email bị trùng lặp trong file (${email})`
            );
          } else {
            seenEmails.add(email);
            normalizedRow.email = email;
          }
        }

        if (normalizedRow.role) {
          const roleText = normalizedRow.role.toLowerCase();
          if (roleText === "admin") normalizedRow.role = 1;
          else if (roleText === "user") normalizedRow.role = 0;
          else if (roleText === "ctv") normalizedRow.role = 2;
          else if (!Number.isNaN(Number(roleText))) {
            normalizedRow.role = Number(roleText);
          }
        }

        if (![0, 1, 2].includes(Number(normalizedRow.role))) {
          rowErrors.push(
            `Dòng ${rowNumber}: Role phải là 0 (User), 1 (Admin) hoặc 2 (CTV)`
          );
        } else {
          normalizedRow.role = Number(normalizedRow.role);
        }

        if (normalizedRow.password && normalizedRow.password.length < 6) {
          rowErrors.push(`Dòng ${rowNumber}: Mật khẩu phải có ít nhất 6 ký tự`);
        }

        if (normalizedRow.premium) {
          const premiumText = normalizedRow.premium.toLowerCase();
          normalizedRow.premium = premiumText === "true" || premiumText === "1";
        } else {
          normalizedRow.premium = false;
        }

        normalizedUsers.push(normalizedRow);
      });

      if (rowErrors.length > 0) {
        toast.error(
          rowErrors[0] || "File Excel có lỗi. Vui lòng kiểm tra lại."
        );
        console.error("Import errors:", rowErrors);
        return;
      }

      const response = await importUsersService(normalizedUsers);
      toast.success(response?.message || "Import người dùng thành công");
      handleFetch();
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message) {
        toast.error(message);
      } else {
        toast.error("Import người dùng thất bại");
      }
    } finally {
      setIsImporting(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleBulkPremiumFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUpdatingPremium(true);
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (!rows || rows.length === 0) {
        toast.error("File Excel không có dữ liệu");
        return;
      }

      // Extract emails from Excel
      const emails = [];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      rows.forEach((row, index) => {
        const rowNumber = index + 2; // header is row 1
        let email = "";

        // Try to find email column (case-insensitive)
        const rowKeys = Object.keys(row);
        for (const key of rowKeys) {
          const normalizedKey = key.toLowerCase().trim();
          if (
            normalizedKey === "email" ||
            normalizedKey === "e-mail" ||
            normalizedKey === "mail"
          ) {
            email = String(row[key] || "")
              .trim()
              .toLowerCase();
            break;
          }
        }

        // If no email column found, try to use first column
        if (!email && rowKeys.length > 0) {
          const firstValue = String(row[rowKeys[0]] || "").trim();
          if (emailRegex.test(firstValue)) {
            email = firstValue.toLowerCase();
          }
        }

        if (email && emailRegex.test(email)) {
          emails.push(email);
        } else if (email) {
          console.warn(`Dòng ${rowNumber}: Email không hợp lệ: ${email}`);
        }
      });

      if (emails.length === 0) {
        toast.error("Không tìm thấy email hợp lệ nào trong file Excel");
        return;
      }

      // Call API to bulk update premium
      const response = await bulkUpdatePremium(emails);
      const result = response?.data || response;

      // Show success message with summary
      if (result.summary) {
        const summary = result.summary;
        let message = `Đã xử lý ${summary.totalProcessed} email. `;
        message += `Cập nhật thành công ${summary.updatedCount} người dùng. `;

        if (summary.alreadyPremiumCount > 0) {
          message += `${summary.alreadyPremiumCount} người dùng đã có premium. `;
        }

        if (summary.notFoundCount > 0) {
          message += `${summary.notFoundCount} email không tồn tại trong hệ thống.`;
        }

        toast.success(message, {
          autoClose: 5000,
        });

        // Show details if available
        if (result.details) {
          const details = result.details;
          if (details.notFoundEmails && details.notFoundEmails.length > 0) {
            console.warn(
              "Emails không tìm thấy:",
              details.notFoundEmails.slice(0, 10)
            );
          }
          if (
            details.alreadyPremiumEmails &&
            details.alreadyPremiumEmails.length > 0
          ) {
            console.info(
              "Emails đã có premium:",
              details.alreadyPremiumEmails.slice(0, 10)
            );
          }
        }
      } else {
        toast.success(result?.message || "Cập nhật premium thành công");
      }

      // Refresh user list
      handleFetch();
    } catch (error) {
      const message = error?.response?.data?.message;
      if (message) {
        toast.error(message);
      } else {
        toast.error("Cập nhật premium hàng loạt thất bại");
      }
      console.error("Bulk premium update error:", error);
    } finally {
      setIsUpdatingPremium(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleDownloadPremiumTemplate = () => {
    const sampleData = [
      {
        email: "example1@gmail.com",
      },
      {
        email: "example2@gmail.com",
      },
      {
        email: "example3@gmail.com",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Emails");
    XLSX.writeFile(workbook, `pimax_bulk_premium_template_${Date.now()}.xlsx`);
  };

  const handleInsertUser = async () => {
    if (validateForm()) {
      try {
        const res = await createUser(formData);
        if (res && res.data) {
          setListUsers([res.data, ...listUsers]);
          handleFetch();
          toast.success(res?.message);
          setFormData({
            email: "",
            password: "",
            role: "",
            fullName: "",
            phoneNumber: "",
            yearOfBirth: "",
            city: "",
            school: "",
          });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message);
      }
    }
  };

  const handleUpdateUser = async () => {
    if (validateForm()) {
      try {
        const res = await createUser(formData);
        if (res && res.data) {
          setListUsers(
            listUsers.map((e) => (e._id === res.data?._id ? res.data : e))
          );
          handleFetch();
          toast.success(res?.message);
          setFormData({
            email: "",
            password: "",
            role: "",
            fullName: "",
            phoneNumber: "",
            yearOfBirth: "",
            city: "",
            school: "",
            courses: [], // Reset courses
          });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message);
      }
    }
  };

  const validateForm = () => {
    if (!formData.email) {
      toast.error("Vui lòng nhập email");
      return false;
    }

    if (!formData.password && !isEditing) {
      toast.error("Vui lòng nhập password");
      return false;
    }

    if (!formData.fullName) {
      toast.error("Vui lòng nhập tên người dùng");
      return false;
    }

    if (!formData.phoneNumber) {
      toast.error("Vui lòng nhập số điện thoại");
      return false;
    }

    if (!formData.yearOfBirth) {
      toast.error("Vui lòng nhập năm sinh");
      return false;
    }

    if (!formData.city) {
      toast.error("Vui lòng nhập thành phố");
      return false;
    }

    if (!formData.school) {
      toast.error("Vui lòng nhập trường học");
      return false;
    }

    if (![0, 1].includes(formData.role)) {
      toast.error("Vui lòng chọn role");
      return false;
    }

    // Validate courses
    // if (!formData.courses || formData.courses.length === 0) {
    //   toast.error("Vui lòng chọn ít nhất một khóa học");
    //   return false;
    // }

    return true;
  };

  const handleChangeInputUser = (event) => {
    let { name, value } = event.target;
    if (name === "role") {
      const role = value === "Admin" ? 1 : 0;
      setFormData({
        ...formData,
        [name]: role,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    if (searchQuery) {
      setIsSearch(true);
    } else {
      setIsSearch(false);
    }
    setCurrentPage(1); // Reset page on search
    handleFetch(); // Fetch data with query
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (searchQuery) {
        setIsSearch(true);
      } else {
        setIsSearch(false);
      }
      handleSearch(); // Trigger search when Enter is pressed
    }
  };

  const handleTogglePremium = async (id, premium) => {
    try {
      const res = await activePremium(id, !premium);
      toast.success(res.message);
      setListUsers(
        listUsers.map((user) =>
          user?._id === id
            ? {
                ...user,
                premium: !user?.premium,
              }
            : user
        )
      );
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  return (
    <div className="p-[30px] overflow-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Quản Lý Người Dùng
      </h2>

      {/* user Form */}
      <UserForm
        isEditing={isEditing}
        formData={formData}
        handleInsertUser={handleInsertUser}
        handleChangeInputUser={handleChangeInputUser}
        handleUpdateUser={handleUpdateUser}
      />
      {/* Search Input */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Tìm kiếm người dúng.."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress} // Listen for Enter key
          className="w-96 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring"
        />
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Search
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleImportClick}
            disabled={isImporting}
            className="px-4 py-2 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-colors disabled:opacity-60"
          >
            {isImporting ? "Đang import..." : "Import Excel"}
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
            type="button"
          >
            Tải template
          </button>
          <div className="border-l border-gray-300 h-6 mx-2"></div>
          <button
            onClick={handleBulkPremiumClick}
            disabled={isUpdatingPremium}
            className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors disabled:opacity-60"
          >
            {isUpdatingPremium
              ? "Đang cập nhật..."
              : "Cập nhật Premium hàng loạt"}
          </button>
          <button
            onClick={handleDownloadPremiumTemplate}
            className="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors"
            type="button"
          >
            Tải template Premium
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        <input
          ref={premiumFileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleBulkPremiumFileChange}
          className="hidden"
        />
      </div>

      {/* user Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          {" "}
          {/* Thêm cuộn dọc nếu có quá nhiều hàng */}
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-3 text-left">No</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Full name</th>
                <th className="p-3 text-left">Role</th>
                {/* <th className="p-3 text-left">Courses</th> */}
                <th className="p-3 text-left">Premium</th>
                <th className="p-3 text-left">Created At</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listUsers.length > 0 &&
                listUsers.map((user, index) => (
                  <tr
                    key={user?._id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <Tooltip title={user?._id} placement="top">
                      <td className="p-3">
                        {" "}
                        {user?._id?.slice(0, 5)}...{user?._id?.slice(-5)}
                      </td>
                    </Tooltip>
                    <td className="p-3">{user?.email}</td>
                    <td className="p-3">{user?.fullName}</td>
                    <td className="p-3">
                      {user?.role === 1 ? "Admin" : "User"}
                    </td>
                    {/* <td className="p-3">{renderCourses(user?.courses)}</td> */}
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user?.premium
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {user?.premium ? "Premium" : "Miễn Phí"}
                      </span>
                    </td>
                    <td className="p-3">
                      {new Date(user?.createdAt).toLocaleDateString(
                        "vi-VN",
                        configDate
                      )}
                    </td>

                    <td className="p-3">
                      <div className="flex items-center justify-center space-x-2 h-full min-h-[40px]">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-500 hover:text-blue-700 transition"
                          title="Edit User"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteUser(user?._id, user?.fullName)
                          }
                          className="text-red-500 hover:text-red-700 transition"
                          title="Delete user"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleTogglePremium(user?._id, user?.premium)
                          }
                          className={`${
                            user?.premium === true
                              ? "text-red-500 hover:text-red-700"
                              : "text-green-500 hover:text-green-700"
                          } transition`}
                          title={
                            user?.premium === true ? "Deactivate" : "Activate"
                          }
                        >
                          {user?.premium === true ? (
                            <XCircle className="w-5 h-5" />
                          ) : (
                            <CheckCircle2 className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between p-4 items-center">
          <span>
            Page {isSearch ? "1 / 1" : `${currentPage} / ${totalPages}`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1 || isSearch}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((p) => (p < totalPages ? p + 1 : p))
              }
              disabled={currentPage === totalPages || isSearch}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, userId: null, userName: "" })
        }
        onConfirm={confirmDeleteUser}
        title="Xác nhận xóa người dùng"
        message="Bạn có chắc chắn muốn xóa người dùng này?"
        itemName={deleteModal.userName}
      />
    </div>
  );
}
